/**
 * One event page's content: picks the current language's event, writes its meta tags, and
 * renders `./EventDetails.tsx`.
 *
 * Rendered by `src/pages/about-us/news-events/[eventSlug].tsx` with the build-time props from
 * `src/contentful/event/getContentfulEventDetails.ts`, `getContentfulUpcomingEvents.ts` and
 * `src/contentful/blog/getContentfulBlogEntries.tsx`.
 *
 * The meta tags are the event's `title` and `description`, the hero image as `imageUrl`, the
 * path `/about-us/news-events/<slug>` and Ryerson's Organization JSON-LD (`company: true`). The
 * event has no separate SEO fields, and a missing description gives `description: null`. Reading
 * `heroImage.file.url` throws when the hero image didn't resolve; the build's props
 * serialisation fails first in that case (`src/contentful/event/helpers/index.ts`).
 */
import React from "react";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import {
	ProcessedContentfulEventDetailsFieldsByLanguage,
	ProcessedContentfulUpcomingEventsByLanguage,
} from "@contentful/event/types";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { Language } from "@ryerson/frontend.common";
import EventDetails from "./EventDetails";
import { TranslatedBlogEntries } from "@contentful/blog/types";

/** The `[eventSlug]` page's own props, passed through unchanged. */
interface EventDetailsContainerProps {
	contentfulEventDetailsByLanguage: ProcessedContentfulEventDetailsFieldsByLanguage;
	allUpcomingEventsByLanguage: ProcessedContentfulUpcomingEventsByLanguage;
	contentfulBlogEntries: TranslatedBlogEntries;
}

/** Writes the event's meta tags and renders the event page body in the current language. */
const EventDetailsContainer: React.FC<EventDetailsContainerProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const contentfulEventDetailsForLanguage = props.contentfulEventDetailsByLanguage[language];

	let metaObj: LanguageContent<MetaContent> = {
		en: {
			title: "",
			description: "",
			url: "",
			company: true,
		},
		fr: {
			title: "",
			description: "",
			url: "",
			company: true,
		},
		es: {
			title: "",
			description: "",
			url: "",
			company: true,
		},
	};
	let obj = {
		title: contentfulEventDetailsForLanguage.title,
		description: contentfulEventDetailsForLanguage.description,
		imageUrl: contentfulEventDetailsForLanguage.heroImage.file.url,
		url: "/about-us/news-events/" + contentfulEventDetailsForLanguage.slug,
		company: true,
	};

	// Only the current language's entry is filled; it is the only one read below.
	if (language === Language.en) {
		metaObj.en = obj;
	} else if (language === Language.fr) {
		metaObj.fr = obj;
	} else {
		metaObj.es = obj;
	}

	return (
		<>
			{/* NOTE: the metaObj[language] will not work for SEO for following reasons:
				1. HasMounted blocks server render, so HTML (including meta tags) is empty → bad for SEO
				2. Localization comes from useState + useContext, which only works after hydration
				3. metaObj tags set via metaObj[language] rely on client-only state, so not seen by crawlers
				
				For SSG and SEO, we will need:
				1. Remove HasMounted and address the underlying issue
				2. Determine how to handle localization when deciding what crawlers should see in the meta tags
				3. Pass localization as a prop, not via useContext
				*/}

			<MetaHead content={metaObj[language]} />
			<EventDetails
				contentfulEventDetailsForLanguage={contentfulEventDetailsForLanguage}
				allUpcomingEventsByLanguage={props.allUpcomingEventsByLanguage}
				contentfulBlogEntries={props.contentfulBlogEntries}
			/>
		</>
	);
};

export default EventDetailsContainer;
